import { call, fork, put, takeLatest } from 'redux-saga/effects';
import { FormScopeEnum, FormSubtypeEnum, FormTypeEnum } from '@dev/ee-api/ee-api-models';
import { push } from 'redux-first-history';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import i18n from '@/../i18n';
import { copyForm, deleteArrayForms, deleteForm, loadForms } from '@/services/ApiCategories/forms';
import { HideToast, ShowToast } from '@/store/toast/actions';
import {
    ArrayOfFormsDeleteError,
    ArrayOfFormsDeleteSuccess,
    FormDeleteError,
    FormDeleteSuccess,
    LoadingFormsError,
    LoadingFormsSuccess,
    CheckIfHaveClassicFormsError,
    CheckIfHaveClassicFormsSuccess,
} from '@/store/forms/actions';
import {
    ArrayOfFormsDeleteStartAction,
    CopyFormStartAction,
    FormActionTypes,
    FormDeleteStartAction,
} from '@/store/forms/types';
import { FormsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { errorToast } from '@/sagas/commonGenerators/errorToast';
import { Logger } from '@/services/errors/appLogger';

function* ShowErrorToast(e: any): any {
    Logger.error(e);
    yield put(
        ShowToast({
            severity: 'error',
            message: `Error: ${e?.response?.data?.Error ?? e.message}`,
            duration: 6000,
            onClose: HideToast,
        })
    );
}

function* checkIfHaveClassicForms(): any {
    const params = {
        offset: 0,
        limit: 1,
        formTypes: [FormTypeEnum.Embedded],
        formScope: FormScopeEnum.Private,
    };

    try {
        const list = yield call(loadForms, params);
        const formsCount = Number(list.headers['x-total-found-count']);

        yield put(CheckIfHaveClassicFormsSuccess(!!formsCount));
    } catch (e) {
        yield ShowErrorToast(e);
        yield put(CheckIfHaveClassicFormsError());
    }
}

function* fetchFormList(action): any {
    const { payload } = action;

    const offset = payload.page === 1 ? 0 : (payload.page - 1) * payload.limit;
    const params = {
        type: payload.type,
        offset,
        limit: payload.limit,
        search: payload.search,
        formsOrder: payload.formsOrder,
        formTypes: payload.formTypes,
        formScope: FormScopeEnum.Private,
        formSubtypes: payload.formSubtypes ?? [FormSubtypeEnum.Popup, FormSubtypeEnum.Inline],
    };

    try {
        const list = yield call(loadForms, params);
        const formsList = list.data;
        const formsCount = Number(list.headers['x-total-found-count']);

        const forms = [...formsList.Forms, ...formsList.GlobalForms];
        const globalFormsCount = formsList.GlobalForms.length;

        yield put(LoadingFormsSuccess(forms, formsCount, globalFormsCount));
    } catch (e) {
        yield ShowErrorToast(e);

        yield put(LoadingFormsError());
    }
}

function* deleteSingleForm(action: FormDeleteStartAction): any {
    const { payload } = action;

    try {
        yield call(deleteForm, payload.formName);

        yield put(FormDeleteSuccess(payload.formName));
        yield put(
            ShowToast({
                message: i18n.t('formdeleted'),
                severity: 'info',
                duration: 3000,
                position: 'bottom-left',
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield ShowErrorToast(e);
        yield put(FormDeleteError());
    }
}

function* deleteArrayOfForms(action: ArrayOfFormsDeleteStartAction): any {
    const { payload } = action;

    try {
        yield call(deleteArrayForms, payload.formNames);

        yield put(ArrayOfFormsDeleteSuccess(payload.formNames));

        yield put(
            ShowToast({
                message: i18n.t('formsdeleted'),
                severity: 'info',
                duration: 3000,
                position: 'bottom-left',
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield ShowErrorToast(e);
        yield put(ArrayOfFormsDeleteError());
    }
}

function* copy({ payload }: CopyFormStartAction): any {
    const { formName } = payload;
    try {
        const data = yield call(copyForm, formName);
        const url = FormsPaths.FormCreator.replace(`${pathsParams.nameOptional}`, safelyEncodeBase64Url(data.Name));
        yield put(push(url));
    } catch (e) {
        Logger.error(e?.response?.data?.Error ?? e);
        yield call(errorToast, e);
    }
}

function* watchCheckIfHaveClassicForms(): any {
    yield takeLatest(FormActionTypes.CHECK_IF_HAVE_CLASSIC_FORMS_START, checkIfHaveClassicForms);
}

function* watchDeleteSingleForm(): any {
    yield takeLatest('@@formList/DELETE_START', deleteSingleForm);
}

function* watchFetchFormList(): any {
    yield takeLatest('@@formList/LOADING_START', fetchFormList);
}

function* watchDeleteArrayOfForms(): any {
    yield takeLatest('@@formList/DELETE_ARRAY_START', deleteArrayOfForms);
}

function* watchCopyForm(): any {
    yield takeLatest(FormActionTypes.COPY_START, copy);
}

const formListSagas = [
    fork(watchFetchFormList),
    fork(watchDeleteSingleForm),
    fork(watchDeleteArrayOfForms),
    fork(watchCopyForm),
    fork(watchCheckIfHaveClassicForms),
];

export default formListSagas;
